import React, { useEffect, useState } from "react";
import axios from "axios";
import { Check, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_PLANS = [
  {
    id: "communication",
    name: "Communication Workshop",
    price_inr: 4999,
    amount_paise: 499900,
    duration: "Half-day intensive",
    features: [
      "Confident public speaking",
      "Business communication frameworks",
      "Live practice & feedback",
      "Digital workbook",
    ],
  },
  {
    id: "grooming",
    name: "Grooming & Etiquette Workshop",
    price_inr: 5999,
    amount_paise: 599900,
    duration: "Full-day workshop",
    features: [
      "Professional grooming standards",
      "Corporate etiquette",
      "Dining & networking manners",
      "Personal branding kit",
    ],
  },
  {
    id: "interview",
    name: "Interview Prep",
    price_inr: 3999,
    amount_paise: 399900,
    duration: "3-session program",
    features: [
      "Mock interviews (HR + Technical)",
      "Resume review & rewrite",
      "STAR-method answer coaching",
      "NLP confidence tools",
    ],
  },
];

const TIME_SLOTS = [
  "Morning (9 AM – 12 PM)",
  "Afternoon (12 PM – 4 PM)",
  "Evening (4 PM – 7 PM)",
  "Flexible / Ruchika to suggest",
];

const todayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0];
};

const minDateISO = () => new Date().toISOString().split("T")[0];

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFallbackBanner, setShowFallbackBanner] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [activePlan, setActivePlan] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_date: todayISO(),
    preferred_time: TIME_SLOTS[0],
    notes: "",
  });

  useEffect(() => {
    let mounted = true;
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));

    (async () => {
      const attempt = async () => {
        const r = await axios.get(`${API}/pricing`);
        return r.data.plans || [];
      };

      try {
        const plansList = await attempt();
        if (!mounted) return;
        setPlans(plansList);
      } catch (e1) {
        console.error("Pricing fetch attempt 1 failed:", e1);
        await wait(1000);
        try {
          const plansList = await attempt();
          if (!mounted) return;
          setPlans(plansList);
        } catch (e2) {
          console.error("Pricing fetch attempt 2 failed:", e2);
          if (!mounted) return;
          setPlans(FALLBACK_PLANS);
          setShowFallbackBanner(true);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const openBooking = (plan) => {
    setActivePlan(plan);
    setDialogOpen(true);
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const resetForm = () =>
    setForm({
      name: "",
      email: "",
      phone: "",
      preferred_date: todayISO(),
      preferred_time: TIME_SLOTS[0],
      notes: "",
    });

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!activePlan) return;
    if (
      !form.name ||
      !form.email ||
      !form.phone ||
      !form.preferred_date ||
      !form.preferred_time
    ) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      // 1) Record the booking intent + notify Ruchika
      await axios.post(`${API}/booking/intent`, {
        plan_id: activePlan.id,
        name: form.name,
        email: form.email,
        phone: form.phone,
        preferred_date: form.preferred_date,
        preferred_time: form.preferred_time,
        notes: form.notes,
      });

      // 2) Create Razorpay order
      const orderRes = await axios.post(`${API}/razorpay/order`, {
        plan_id: activePlan.id,
        amount: activePlan.amount_paise,
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
      });
      const data = orderRes.data;

      // 3) Load checkout.js and open modal
      const ok = await loadRazorpayScript();
      if (!ok) {
        toast.error("Could not load Razorpay. Please try again.");
        return;
      }

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Behavioural Training Spectrum",
        description: activePlan.name,
        order_id: data.order_id,
        theme: { color: "#FFDA00" },
        prefill: {
          name: form.name,
          email: form.email,
          contact: form.phone,
        },
        notes: {
          plan_id: activePlan.id,
          plan_name: activePlan.name,
          preferred_date: form.preferred_date,
          preferred_time: form.preferred_time,
        },
        handler: async (response) => {
          try {
            await axios.post(`${API}/razorpay/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: activePlan.id,
              customer_name: form.name,
              customer_email: form.email,
            });
            toast.success("Payment successful", {
              description: `Your ${activePlan.name} booking is confirmed. Ruchika will reach out to lock the slot.`,
            });
            setDialogOpen(false);
            resetForm();
          } catch (err) {
            toast.error(
              "Payment received but verification failed. Please contact us."
            );
          }
        },
        modal: {
          ondismiss: () => {
            toast.message("Payment cancelled", {
              description: "You can re-open the checkout anytime.",
            });
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error("Payment failed", {
          description:
            resp?.error?.description || "Please try again or use another method.",
        });
      });
      rzp.open();
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Could not start checkout. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="border-b border-black/10 bg-white"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="overline mb-3">Pricing</div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-black leading-[0.98] max-w-2xl">
              Open workshops. Transparent pricing.
            </h2>
          </div>
          <p className="max-w-md text-neutral-700 text-sm md:text-base">
            Book individually or bring these programs in-house. Corporate
            bookings are custom-priced — talk to us.
          </p>
        </div>

        {showFallbackBanner && (
          <div
            data-testid="pricing-fallback-banner"
            className="mb-6 border border-black bg-[color:var(--bts-yellow)] px-4 py-3 flex items-start justify-between gap-4"
          >
            <p className="text-sm text-black">
              Some content may be hidden by a browser extension. Please disable
              your ad-blocker for this site or try incognito mode.
            </p>
            <button
              type="button"
              data-testid="pricing-fallback-dismiss"
              onClick={() => setShowFallbackBanner(false)}
              className="text-black text-sm font-bold shrink-0 hover:underline"
              aria-label="Dismiss notice"
            >
              Dismiss
            </button>
          </div>
        )}

        {loading ? (
          <div className="py-24 flex justify-center">
            <Loader2 className="animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-black">
            {plans.map((p, i) => {
              const highlight = i === 1;
              return (
                <div
                  key={p.id}
                  data-testid={`pricing-card-${p.id}`}
                  className={`p-7 md:p-9 flex flex-col ${
                    i > 0 ? "md:border-l md:border-black" : ""
                  } ${
                    highlight
                      ? "bg-[color:var(--bts-yellow)] text-black"
                      : "bg-white"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="overline">{p.duration}</span>
                    {highlight && (
                      <span className="text-[10px] tracking-[0.2em] uppercase font-bold bg-black text-[color:var(--bts-yellow)] px-2 py-1">
                        Most booked
                      </span>
                    )}
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl text-black mt-4 leading-tight">
                    {p.name}
                  </h3>
                  <div className="mt-6 flex items-end gap-1">
                    <span className="font-display text-5xl md:text-6xl text-black">
                      ₹{p.price_inr.toLocaleString("en-IN")}
                    </span>
                    <span className="text-xs mb-2 text-neutral-700">
                      per seat
                    </span>
                  </div>

                  <ul className="mt-8 space-y-3">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <Check size={16} className="mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    data-testid={`pricing-cta-${p.id}`}
                    onClick={() => openBooking(p)}
                    className={`mt-10 ${
                      highlight ? "btn-secondary" : "btn-primary"
                    } justify-center`}
                  >
                    Book & Pay <ArrowUpRight size={16} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-6 text-xs text-neutral-500">
          Prices are indicative for open workshops. For corporate rollouts and
          POSH engagements, request a custom quote.
        </p>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="max-w-lg rounded-none border border-black bg-white p-0"
          data-testid="booking-dialog"
        >
          <div className="bg-[color:var(--bts-yellow)] px-6 py-5 border-b border-black">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-black tracking-tight">
                Book your seat
              </DialogTitle>
              <DialogDescription className="text-black/70 text-sm mt-1">
                {activePlan
                  ? `${activePlan.name} — ₹${activePlan.price_inr.toLocaleString(
                      "en-IN"
                    )}`
                  : ""}
              </DialogDescription>
            </DialogHeader>
          </div>

          <form onSubmit={submitBooking} className="p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <BField
                label="Full name *"
                testid="booking-name"
                value={form.name}
                onChange={(v) => update("name", v)}
                placeholder="Your name"
              />
              <BField
                label="Email *"
                type="email"
                testid="booking-email"
                value={form.email}
                onChange={(v) => update("email", v)}
                placeholder="you@company.com"
              />
              <BField
                label="Phone *"
                testid="booking-phone"
                value={form.phone}
                onChange={(v) => update("phone", v)}
                placeholder="+91 …"
              />
              <BField
                label="Preferred date *"
                type="date"
                testid="booking-date"
                value={form.preferred_date}
                onChange={(v) => update("preferred_date", v)}
                min={minDateISO()}
              />
            </div>

            <div>
              <label className="overline block mb-2">Preferred time *</label>
              <select
                data-testid="booking-time"
                value={form.preferred_time}
                onChange={(e) => update("preferred_time", e.target.value)}
                className="w-full border border-black bg-white py-2 px-3 focus:outline-none text-sm"
              >
                {TIME_SLOTS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="overline block mb-2">Anything to add</label>
              <textarea
                data-testid="booking-notes"
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                rows={3}
                placeholder="Special requests, team size, etc."
                className="w-full border border-black p-3 bg-white text-sm focus:outline-none"
              />
            </div>

            <div className="pt-2 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                data-testid="booking-cancel"
                className="btn-secondary justify-center"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="booking-submit"
                disabled={submitting}
                className="btn-primary justify-center"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Loading
                  </>
                ) : (
                  <>
                    Proceed to pay <ArrowUpRight size={16} />
                  </>
                )}
              </button>
            </div>

            <p className="text-[11px] text-neutral-500 pt-1">
              Your details are shared only with Ruchika. She will confirm the
              exact slot with you after payment.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

const BField = ({
  label,
  testid,
  value,
  onChange,
  type = "text",
  placeholder,
  min,
}) => (
  <div>
    <label className="overline block mb-2">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      min={min}
      className="w-full border border-black bg-white py-2 px-3 focus:outline-none text-sm placeholder:text-neutral-400"
    />
  </div>
);

export default Pricing;
